import { Button } from '@repo/ui/components/button';
import { Card, CardContent } from '@repo/ui/components/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/components/dropdown-menu';
import { Calendar, MoreVertical, Pencil, Trash2 } from 'lucide-react';

import type { OccasionItem } from '@/hooks/queries/use-relationship';
import { useCountdown } from '@/hooks/use-countdown';
import { parseLocalDate } from '@/lib/utils';

export interface OccasionItemCardProps {
  item: OccasionItem;
  onEdit?: (item: OccasionItem) => void;
  onDelete?: (item: OccasionItem) => void;
}

function formatDate(dateString: string): string {
  const date = parseLocalDate(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
  });
}

export function OccasionItemCard({
  item,
  onEdit,
  onDelete,
}: OccasionItemCardProps) {
  const countdown = useCountdown(item.date);
  const showActions = onEdit || onDelete;

  const getCountdownText = () => {
    if (!countdown) {
      return null;
    }
    if (countdown.isToday) {
      return 'Hoje!';
    }
    if (countdown.days === 1) {
      return 'Amanhã';
    }
    return `${countdown.days} dias`;
  };

  const countdownText = getCountdownText();

  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className="bg-primary/10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
          <Calendar className="text-primary h-5 w-5" />
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="text-foreground font-medium">{item.title}</h3>
          <p className="text-muted-foreground text-sm">
            {formatDate(item.date)}
          </p>
        </div>

        {countdownText && (
          <div className="shrink-0 text-right">
            <span
              className={`text-sm font-medium ${
                countdown?.isToday ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {countdownText}
            </span>
          </div>
        )}

        {showActions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Abrir menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(item)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Editar
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(item)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </CardContent>
    </Card>
  );
}
